import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Fragment, Slice } from '@tiptap/pm/model';
import type { Node as PMNode, ResolvedPos } from '@tiptap/pm/model';
import { ReplaceStep, ReplaceAroundStep } from '@tiptap/pm/transform';
import { isHistoryTransaction } from '@tiptap/pm/history';
import { findTemplateFieldNestingViolation } from '@confinion/confideck-shared';
import { ALL_TEMPLATE_FIELD_NAMES } from '../../doc-engine/templateFields';
import { FORM_FIELD_LINE_NODE_NAME, FORM_FIELD_NODE_NAME } from '../nodes/formField/constants';
import { isSystemTr } from './shared';

/** True when `node`, or anything inside it, may not sit under `ancestors` (outermost first). */
export type TNestingViolationCheck = (node: PMNode, ancestors: readonly PMNode[]) => boolean;

// A card and its line hold only their field, so they may not go where it may not: into another field.
const FIELD_WRAPPER_NAMES = new Set([FORM_FIELD_NODE_NAME, FORM_FIELD_LINE_NODE_NAME]);

export const violatesTemplateFieldNesting: TNestingViolationCheck = (node, ancestors) =>
  findTemplateFieldNestingViolation(node, ancestors) !== undefined ||
  (FIELD_WRAPPER_NAMES.has(node.type.name) &&
    ancestors.some((ancestor) => ALL_TEMPLATE_FIELD_NAMES.has(ancestor.type.name)));

export const ancestorsAt = ($pos: ResolvedPos): PMNode[] =>
  Array.from({ length: $pos.depth + 1 }, (_, depth) => $pos.node(depth));

// Only what the step wrote - never the whole document on a keystroke.
// The written range, not step.slice: copied content carries ancestors as open context that merges away.
const stepWritesViolation = (
  step: ReplaceStep | ReplaceAroundStep,
  docBefore: PMNode,
  resultDoc: PMNode,
  isViolation: TNestingViolationCheck
): boolean => {
  // The nodes that held the whole replaced range: what the step writes belongs inside them, even when
  // the fitter splits one of them (several paragraphs pasted into an inline field) and moves it out.
  const $from = docBefore.resolve(step.from);
  const enclosing = ancestorsAt($from).slice(0, $from.sharedDepth(step.to) + 1);
  const insertedEnd = Math.min(step.getMap().map(step.to, 1), resultDoc.content.size);
  let found = false;
  resultDoc.nodesBetween(step.from, insertedEnd, (node, pos) => {
    if (found) return false;
    if (pos < step.from) return true;
    const isWhole = pos + node.nodeSize <= insertedEnd || node.isText;
    // A node that runs on past the written range was opened by the step: only the node itself is new.
    const written = isWhole ? node : node.type.create(node.attrs, null, node.marks);
    found = isViolation(written, ancestorsAt(resultDoc.resolve(pos))) || isViolation(written, enclosing);
    return !isWhole;
  });
  return found;
};

/** Replace each forbidden node with its content: textblocks become paragraphs (keeps the text),
 *  wrappers are flattened, childless atoms are dropped. */
const unwrapForbidden = (fragment: Fragment, isForbidden: (node: PMNode) => boolean): Fragment => {
  const out: PMNode[] = [];
  fragment.forEach((child) => {
    const content = unwrapForbidden(child.content, isForbidden);
    if (!isForbidden(child)) {
      out.push(child.copy(content));
    } else if (child.isTextblock) {
      out.push(child.type.schema.nodes.paragraph.create(null, content, child.marks));
    } else {
      content.forEach((n) => out.push(n));
    }
  });
  return Fragment.fromArray(out);
};

// Blocks every insertion that `isViolation` refuses, across every path: toolbar, paste, drag-drop and programmatic insertContent.
const getNestedInsertionGuardPlugin = (isViolation: TNestingViolationCheck, pluginKey: string): Plugin => {
  return new Plugin({
    key: new PluginKey(pluginKey),
    props: {
      // Copied content carries its guarded ancestor in the slice and the fitter can materialize
      // it as real nesting; unwrap it and let the default paste path insert the content.
      transformPasted(slice, view) {
        const ancestors = ancestorsAt(view.state.selection.$from);
        // The node alone: the unwrap keeps its children and decides each of them in turn.
        const isForbidden = (node: PMNode) =>
          !node.isText && isViolation(node.type.create(node.attrs, null, node.marks), ancestors);
        let hasForbidden = false;
        slice.content.descendants((node) => {
          if (isForbidden(node)) hasForbidden = true;
          return !hasForbidden;
        });
        if (!hasForbidden) return slice;

        const cleaned = unwrapForbidden(slice.content, isForbidden);
        // Empty result: keep the original for the filter - an empty paste would delete the selection.
        if (cleaned.size === 0) return slice;
        return Slice.maxOpen(cleaned);
      },
    },
    filterTransaction(tr) {
      if (!tr.docChanged) return true;
      // System transactions are trusted - the accepted AI apply too: the backend refuses nesting first.
      if (isSystemTr(tr)) return true;
      // Undo and redo only bring back states the guard let through; an undo can re-split fields a delete merged.
      if (isHistoryTransaction(tr)) return true;

      for (let i = 0; i < tr.steps.length; i++) {
        const step = tr.steps[i];
        if (!(step instanceof ReplaceStep) && !(step instanceof ReplaceAroundStep)) continue;

        if (stepWritesViolation(step, tr.docs[i], tr.docs[i + 1] ?? tr.doc, isViolation)) {
          console.warn(`[${pluginKey}] blocked: forbidden node would nest inside its guarded ancestor`);
          return false;
        }
      }
      return true;
    },
  });
};

export default getNestedInsertionGuardPlugin;
